import {buildAddressClean, buildStreetWithHouseNumber, type GeoRefAddress} from "@reva/shared/geo-reference";
import type {OriginalAddress} from "@reva/shared/valuation-data";

import {GeoRefApiInterface} from "../api/interface/georef";
import {generateDefaultQueryError} from "../util/errorUtil";
import {
    addressFromPersistenceLoaded,
    ambiguousAddressFound,
    clearAddress,
    type OrderAddress,
    type PersistenceAddressData,
    setLoading,
    setQueryError,
} from "./addressSlice";
import {selectCredentials} from "./authSlice";
import {abortRunningIndications} from "./indicationRequests";
import {clearAllLocationInfoData} from "./locationInfoSlice";
import {resetMarketDataInput} from "./marketDataInputSlice";
import type {RevaThunkAction} from "./revaHooks";
import {clearIndicationData} from "./valueIndicationSlice";

/**
 * Async thunk to query for an address.
 * If the query was successful and yielded a single address, it is returned as response.
 */
export const searchAddressAsync =
    (addressQuery: string): RevaThunkAction<Promise<GeoRefAddress | null>> =>
    async (dispatch, getState, {env, serviceApi}) => {
        const geoRefInterface = new GeoRefApiInterface(addressQuery);
        if (!geoRefInterface.validateAndDispatchQueryError((queryError) => dispatch(setQueryError(queryError)))) {
            return null;
        }
        dispatch(setLoading());
        const credentials = selectCredentials(getState());
        try {
            if (env.isDevelopmentMode) {
                console.log(`GeoRef query for address: ${addressQuery}`);
            }
            const result = await geoRefInterface.execute(serviceApi, credentials!);
            if (result.ambiguous) {
                dispatch(ambiguousAddressFound({addresses: result.addresses}));
            } else {
                return result.addressMeta;
            }
        } catch (err) {
            dispatch(setQueryError(generateDefaultQueryError(err as Error)));
        }
        return null;
    };

/**
 * Thunk to store the address data from loaded/created persistence.
 */
export const persistenceAddressLoaded =
    (
        addressQuery: string,
        address: GeoRefAddress,
        hasReferenceAddress: boolean,
        referenceAddressComment: string | undefined,
        originalAddress?: OriginalAddress,
    ): RevaThunkAction<void> =>
    (dispatch) => {
        const addressClean = buildAddressClean(address);
        // When loaded from persistence the source address always carries these fields, so they are safe to enforce.
        // Die Hausnummer des Auftrags liegt bei straßenabschnittsgenauen Treffern nur in `houseNumber` (#19746).
        const orderAddress: OrderAddress = originalAddress
            ? {
                  streetAndHouseNumber: buildStreetWithHouseNumber(originalAddress.street, originalAddress.houseNumber),
                  city: originalAddress.city,
                  zipCode: originalAddress.zipCode,
              }
            : {
                  streetAndHouseNumber: buildStreetWithHouseNumber(address.street, address.houseNumber),
                  city: address.municipality!,
                  zipCode: address.postcode!,
              };
        const orderAddressClean = `${orderAddress.streetAndHouseNumber}, ${orderAddress.zipCode} ${orderAddress.city}`;
        const lat = address.lat;
        const lon = address.lon;
        const persistenceAddressData: PersistenceAddressData = {
            addressQuery,
            lat,
            lon,
            address,
            addressClean,
            hasReferenceAddress,
            orderAddress,
            orderAddressClean,
            referenceAddressComment,
        };

        dispatch(addressFromPersistenceLoaded(persistenceAddressData));
    };

/**
 * Thunk to remove a selected distinct address. Also clear address-dependent data.
 */
export const removeDistinctAddress = (): RevaThunkAction<void> => (dispatch) => {
    dispatch(clearAddress());
    // Laufende Indikationen gehören zur alten Adresse — abbrechen, sonst landet ihre Antwort noch
    // nachträglich in der Anzeige (#19374).
    abortRunningIndications();
    // Clear data, which depends on the address
    dispatch(clearAllLocationInfoData());
    dispatch(resetMarketDataInput());
    dispatch(clearIndicationData());
};
